import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, ShoppingCart, BarChart3, Loader2, Save, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "inventory", label: "Products & Inventory", icon: Package },
];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [s, o, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/products"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setProducts(p.data);
    } catch {
      // handled by redirect below
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (admin === false) navigate("/admin");
    if (admin) loadAll();
  }, [admin, navigate, loadAll]);

  if (admin === null || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const doLogout = async () => {
    await logout();
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      {/* topbar */}
      <div className="sticky top-0 z-40 border-b border-[#E4DDD0] bg-cream/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4">
          <div>
            <p className="font-serif text-2xl text-moss">Cured <span className="text-gold">&</span> Clear</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted">Admin Dashboard</p>
          </div>
          <button onClick={doLogout} className="flex items-center gap-2 rounded-sm border border-moss px-4 py-2 text-xs font-mono uppercase tracking-wider text-moss hover:bg-moss hover:text-cream" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-8">
        {/* tabs */}
        <div className="mb-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                tab === t.id ? "bg-moss text-cream" : "border border-[#E4DDD0] text-moss hover:border-gold"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview stats={stats} orders={orders} />}
        {tab === "orders" && <Orders orders={orders} reload={loadAll} />}
        {tab === "inventory" && <Inventory products={products} reload={loadAll} />}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-sm border border-[#E4DDD0] bg-cream-2 p-6">
      <p className="font-mono text-xs uppercase tracking-wider text-muted">{label}</p>
      <p className="mt-2 font-serif text-4xl text-moss">{value}</p>
    </div>
  );
}

function Overview({ stats, orders }) {
  const chartData = orders
    .filter((o) => o.payment_status === "paid")
    .slice(0, 7)
    .reverse()
    .map((o, i) => ({ name: `#${i + 1}`, revenue: o.amount || 0 }));

  return (
    <div data-testid="admin-overview">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Revenue" value={`$${(stats?.revenue || 0).toFixed(2)}`} />
        <Stat label="Paid Orders" value={stats?.paid_orders || 0} />
        <Stat label="Total Orders" value={orders.length} />
        <Stat label="Products" value={stats?.total_products || 0} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <div className="rounded-sm border border-[#E4DDD0] bg-cream-2 p-6">
          <p className="font-serif text-2xl text-moss mb-4">Recent Revenue</p>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4DDD0" />
                <XAxis dataKey="name" stroke="#576056" fontSize={12} />
                <YAxis stroke="#576056" fontSize={12} />
                <Tooltip />
                <Bar dataKey="revenue" fill="#C89D52" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted py-12 text-center">No paid orders yet.</p>
          )}
        </div>

        <div className="rounded-sm border border-[#E4DDD0] bg-cream-2 p-6">
          <p className="font-serif text-2xl text-moss mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-gold" /> Low Stock
          </p>
          {stats?.low_stock?.length ? (
            <ul className="space-y-3">
              {stats.low_stock.map((l, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span className="text-moss">{l.product} · {l.size}</span>
                  <span className="font-mono text-terracotta">{l.inventory} left</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">All products well stocked.</p>
          )}
        </div>
      </div>
    </div>
  );
}

const FULFILL = ["unfulfilled", "processing", "shipped", "delivered"];

function Orders({ orders, reload }) {
  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/fulfillment`, { status });
      toast.success("Order updated");
      reload();
    } catch {
      toast.error("Update failed");
    }
  };

  if (!orders.length)
    return <p className="rounded-sm border border-[#E4DDD0] bg-cream-2 p-12 text-center text-muted" data-testid="admin-orders-empty">No orders yet.</p>;

  return (
    <div className="space-y-4" data-testid="admin-orders">
      {orders.map((o) => (
        <div key={o.id} className="rounded-sm border border-[#E4DDD0] bg-cream-2 p-6" data-testid={`admin-order-${o.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-mono text-xs text-muted">#{o.id.slice(0, 8)} · {new Date(o.created_at).toLocaleString()}</p>
              <p className="mt-1 font-serif text-xl text-moss">{o.customer_name || o.shipping_address?.name || o.email || "Guest"}</p>
              {o.email && <p className="text-xs text-muted">{o.email}</p>}
              {o.shipping_address?.line1 && (
                <p className="mt-2 text-xs text-muted leading-relaxed" data-testid={`order-address-${o.id}`}>
                  Ship to: {o.shipping_address.name ? `${o.shipping_address.name}, ` : ""}
                  {o.shipping_address.line1}{o.shipping_address.line2 ? ` ${o.shipping_address.line2}` : ""},{" "}
                  {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code},{" "}
                  {o.shipping_address.country}
                </p>
              )}
              {o.shipping_note && (
                <p className="mt-1 text-xs italic text-gold" data-testid={`order-note-${o.id}`}>{o.shipping_note}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <span className={`rounded-full px-3 py-1 text-[10px] font-mono uppercase ${o.payment_status === "paid" ? "bg-moss text-cream" : "bg-cream-3 text-muted"}`}>
                  {o.payment_status}
                </span>
                {o.promo_code && (
                  <span className="rounded-full bg-gold/15 px-3 py-1 text-[10px] font-mono uppercase text-gold" data-testid={`order-promo-${o.id}`}>{o.promo_code}</span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="font-serif text-2xl text-moss">${(o.amount || 0).toFixed(2)}</p>
              <select
                value={o.fulfillment_status || "unfulfilled"}
                onChange={(e) => setStatus(o.id, e.target.value)}
                className="mt-2 rounded-sm border border-[#E4DDD0] bg-cream px-3 py-1.5 text-xs font-mono"
                data-testid={`order-status-${o.id}`}
              >
                {FULFILL.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 border-t border-[#E4DDD0] pt-4 space-y-1">
            {o.items?.map((it, i) => (
              <p key={i} className="text-sm text-muted">
                {it.quantity}× {it.name} — {it.size}{it.variant ? ` · ${it.variant}` : ""} <span className="text-moss">${(it.price * it.quantity).toFixed(2)}</span>
              </p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Inventory({ products, reload }) {
  const [drafts, setDrafts] = useState(() => Object.fromEntries(products.map((p) => [p.id, p.sizes])));
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    setDrafts(Object.fromEntries(products.map((p) => [p.id, p.sizes])));
  }, [products]);

  const update = (pid, idx, field, value) => {
    setDrafts((d) => {
      const sizes = d[pid].map((s, i) => (i === idx ? { ...s, [field]: field === "size" || field === "sku" ? value : Number(value) } : s));
      return { ...d, [pid]: sizes };
    });
  };

  const save = async (pid) => {
    setSaving(pid);
    try {
      await api.put(`/products/${pid}`, { sizes: drafts[pid] });
      toast.success("Product saved");
      reload();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory">
      {products.map((p) => (
        <div key={p.id} className="rounded-sm border border-[#E4DDD0] bg-cream-2 p-6" data-testid={`admin-product-${p.id}`}>
          <div className="flex items-center gap-4">
            <img src={p.image} alt={p.name} className="h-16 w-14 rounded-sm object-cover" />
            <div>
              <p className="font-serif text-2xl text-moss">{p.name}</p>
              <p className="font-mono text-xs text-muted">{p.badge}</p>
            </div>
          </div>
          <div className="mt-5 space-y-3">
            {drafts[p.id]?.map((s, idx) => (
              <div key={s.sku} className="grid grid-cols-2 gap-3 sm:grid-cols-4 items-center">
                <div>
                  <label className="font-mono text-[10px] uppercase text-muted">Size</label>
                  <input value={s.size} onChange={(e) => update(p.id, idx, "size", e.target.value)} className="w-full rounded-sm border border-[#E4DDD0] bg-cream px-3 py-2 text-sm" data-testid={`inv-size-${s.sku}`} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase text-muted">Price ($)</label>
                  <input type="number" step="0.01" value={s.price} onChange={(e) => update(p.id, idx, "price", e.target.value)} className="w-full rounded-sm border border-[#E4DDD0] bg-cream px-3 py-2 text-sm" data-testid={`inv-price-${s.sku}`} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase text-muted">Inventory</label>
                  <input type="number" value={s.inventory} onChange={(e) => update(p.id, idx, "inventory", e.target.value)} className="w-full rounded-sm border border-[#E4DDD0] bg-cream px-3 py-2 text-sm" data-testid={`inv-stock-${s.sku}`} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase text-muted">SKU</label>
                  <p className="py-2 font-mono text-xs text-moss">{s.sku}</p>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => save(p.id)}
            disabled={saving === p.id}
            className="btn-gold mt-5 flex items-center gap-2 rounded-sm px-6 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-60"
            data-testid={`inv-save-${p.id}`}
          >
            {saving === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      ))}
    </div>
  );
}
